import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { inputCls, ghostBtnCls, primaryBtnCls, Table, Td, Modal, Field } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const rup = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// preset: "bills" | "outsource" | "ct"
export default function LabBills({ preset = "bills" }) {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [f, setF] = useState({ q: "", reg_no: "", referrer_id: "", centre_id: "", agent_id: "", has_due: false, cancelled: false });
  const [refs, setRefs] = useState([]); const [centres, setCentres] = useState([]); const [agents, setAgents] = useState([]);
  const [cleanup, setCleanup] = useState({ open: false, bill: null, reason: "", confirmed: false, busy: false, error: "" });

  const submitCleanup = async (e) => {
    e.preventDefault();
    if (!cleanup.confirmed) { setCleanup((s) => ({ ...s, error: "Confirm permanent deletion to continue." })); return; }
    setCleanup((s) => ({ ...s, busy: true, error: "" }));
    try {
      await api.post(`/lab/bills/${cleanup.bill.bill_id}/cleanup`, { confirmation: true, reason: cleanup.reason.trim() });
      toast.success("Empty lab bill permanently removed and audited.");
      setCleanup({ open: false, bill: null, reason: "", confirmed: false, busy: false, error: "" });
      load(page);
    } catch (err) { setCleanup((s) => ({ ...s, busy: false, error: apiError(err) })); }
  };

  const titles = { bills: "All Bills", outsource: "Outsource Cases", ct: "CT Scan Cases" };
  const svc = preset === "ct" ? "ct" : undefined;

  useEffect(() => {
    if (preset !== "bills") return;
    Promise.all([api.get("/lab/referrers", { params: { active: true } }), api.get("/lab/centres", { params: { active: true } }), api.get("/lab/agents", { params: { active: true } })])
      .then(([r, c, a]) => { setRefs(r.data.items); setCentres(c.data.items); setAgents(a.data.items); }).catch(() => {});
  }, [preset]);

  const load = async (p = 1) => {
    setItems(null); setPage(p);
    try {
      const params = { page: p, limit: 20, q: f.q || undefined, reg_no: f.reg_no || undefined,
        referrer_id: f.referrer_id || undefined, centre_id: f.centre_id || undefined, agent_id: f.agent_id || undefined,
        has_due: f.has_due || undefined, cancelled: f.cancelled ? true : undefined, service_type: svc };
      if (preset === "outsource") params.service_type = "outsource_lab";
      const { data } = await api.get("/lab/bills", { params });
      setItems(data.items); setTotal(data.total);
    } catch (e) { setError(apiError(e)); }
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [preset]);

  if (!can("lab.orders.view")) return <NoAccess what="lab bills" />;
  const pages = Math.max(1, Math.ceil(total / 20));

  return (
    <div data-testid={`lab-${preset}-page`}>
      <PageHeader overline="Cases" title={titles[preset]}
        subtitle="Historical financial records — filter, view and act on lab bills."
        actions={can("lab.orders.create") && <Link to="/app/lab/new-bill" className={primaryBtnCls} data-testid="new-bill-link">+ New bill</Link>} />

      {preset === "bills" && (
        <form onSubmit={(e) => { e.preventDefault(); load(1); }} className="panel p-4 mb-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          <input className={inputCls} placeholder="Reg. no." value={f.reg_no} onChange={(e) => setF({ ...f, reg_no: e.target.value })} data-testid="filter-regno" />
          <input className={inputCls} placeholder="Patient / mobile / UHID" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="filter-q" />
          <select className={inputCls} value={f.referrer_id} onChange={(e) => setF({ ...f, referrer_id: e.target.value })} data-testid="filter-referrer"><option value="">All referrers</option>{refs.map((r) => <option key={r.referrer_id} value={r.referrer_id}>{r.name}</option>)}</select>
          <select className={inputCls} value={f.centre_id} onChange={(e) => setF({ ...f, centre_id: e.target.value })} data-testid="filter-centre"><option value="">All centres</option>{centres.map((c) => <option key={c.centre_id} value={c.centre_id}>{c.name}</option>)}</select>
          <select className={inputCls} value={f.agent_id} onChange={(e) => setF({ ...f, agent_id: e.target.value })} data-testid="filter-agent"><option value="">All agents</option>{agents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}</select>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.has_due} onChange={(e) => setF({ ...f, has_due: e.target.checked })} data-testid="filter-hasdue" /> Has due</label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.cancelled} onChange={(e) => setF({ ...f, cancelled: e.target.checked })} data-testid="filter-cancelled" /> Cancelled</label>
          <div className="flex gap-2"><button className={primaryBtnCls} data-testid="filter-search">Search</button><button type="button" className={ghostBtnCls} onClick={() => { setF({ q: "", reg_no: "", referrer_id: "", centre_id: "", agent_id: "", has_due: false, cancelled: false }); load(1); }}>Clear</button></div>
        </form>
      )}

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <>
          <Table testid={`lab-${preset}-table`} columns={["Reg. no.", "Date", "Patient", "Referred by", "Total", "Paid", "Discount", "Status", "Actions"]}
            rows={items} empty={<EmptyState title="No cases found" subtitle="Adjust filters or create a new bill." testid={`lab-${preset}-empty`} />}
            renderRow={(b) => (
              <tr key={b.bill_id} data-testid={`lab-bill-row-${b.bill_id}`}>
                <Td className="font-mono text-slate-700">{b.reg_no}</Td>
                <Td className="text-slate-500 text-xs">{(b.created_at || "").slice(0, 10)}</Td>
                <Td className="font-medium text-slate-900">{b.patient_title} {b.patient_name}<span className="block text-xs text-slate-400">{b.patient_phone}</span></Td>
                <Td className="text-slate-600">{b.referrer_name || "—"}</Td>
                <Td className="text-slate-700">{rup(b.net_total)}</Td>
                <Td className="text-slate-700">{rup(b.paid)}</Td>
                <Td className="text-slate-600">{rup(b.total_discount)}</Td>
                <Td>{b.cancelled ? <StatusPill status="cancelled" /> : b.due > 0 ? <span className="text-xs font-medium text-amber-700">Due {rup(b.due)}</span> : <StatusPill status={b.status} />}</Td>
                <Td>
                  <div className="flex items-center gap-3">
                    <Link to={`/app/lab/bills/${b.bill_id}`} className="text-slate-900 underline text-sm" data-testid={`view-bill-${b.bill_id}`}>View bill</Link>
                    {can("lab.billing.manage") && !b.cancelled && (b.paid || 0) <= 0 && !b.report_released && (
                      <button onClick={() => setCleanup({ open: true, bill: b, reason: "", confirmed: false, busy: false, error: "" })}
                        className="text-xs font-medium text-red-600 hover:text-red-800" data-testid={`cleanup-bill-${b.bill_id}`}>Remove test record</button>
                    )}
                  </div>
                </Td>
              </tr>
            )} />
          <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
            <span>{total} cases</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => load(page - 1)} className={ghostBtnCls}>Prev</button>
              <span className="px-3 py-2">Page {page}/{pages}</span>
              <button disabled={page >= pages} onClick={() => load(page + 1)} className={ghostBtnCls}>Next</button>
            </div>
          </div>
        </>
      )}

      <Modal open={cleanup.open} onClose={() => setCleanup((s) => ({ ...s, open: false }))}
        title="Remove empty test lab bill"
        subtitle="Available only when the bill has no payment, sample, results or released report."
        testid="lab-cleanup-modal">
        <form onSubmit={submitCleanup} className="space-y-5">
          <p className="text-sm text-slate-700" data-testid="lab-cleanup-warning">
            Reg. no. <strong>{cleanup.bill?.reg_no}</strong> — {cleanup.bill?.patient_name}. This bill has no protected
            activity and will be permanently deleted. Any linked lab order is unlinked (never deleted).
          </p>
          <Field label="Deletion reason *">
            <textarea required minLength={3} rows={3} className={inputCls} data-testid="lab-cleanup-reason"
              value={cleanup.reason} onChange={(e) => setCleanup((s) => ({ ...s, reason: e.target.value }))} />
          </Field>
          <label className="flex items-start gap-2 text-sm text-slate-700" data-testid="lab-cleanup-confirmation">
            <input type="checkbox" checked={cleanup.confirmed} onChange={(e) => setCleanup((s) => ({ ...s, confirmed: e.target.checked }))} />
            I understand this cannot be undone.
          </label>
          {cleanup.error && <p className="text-sm font-medium text-red-600" data-testid="lab-cleanup-error">{cleanup.error}</p>}
          <button disabled={cleanup.busy} className={primaryBtnCls} data-testid="lab-cleanup-submit">{cleanup.busy ? "Removing…" : "Permanently delete"}</button>
        </form>
      </Modal>
    </div>
  );
}
